import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Plus, ClipboardList, Settings2, Trash2, Pencil, Search, CheckCircle2, Clock, ShieldCheck, ArrowUpDown, QrCode, AlertTriangle } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useDialog } from "@/context/DialogProvider";
import { toast } from "sonner";
import FormTemplateModal from "@/pages/FormTemplateModal";
import FormFillModal from "@/pages/FormFillModal";
import FormQRModal from "@/pages/FormQRModal";

const BACKLOG_OPTIONS = [
  { key: "7", label: "7 days" },
  { key: "30", label: "30 days" },
  { key: "60", label: "60 days" },
  { key: "90", label: "90 days" },
  { key: "all", label: "All time" },
];

const STATUS_META = {
  submitted: { label: "Awaiting verification", cls: "vb-pill warn", icon: Clock },
  verified:  { label: "Verified", cls: "vb-pill ok", icon: CheckCircle2 },
};

function fmtDT(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { year: "numeric", month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}

function fmtAnswer(ans) {
  if (ans?.value === null || ans?.value === undefined || ans?.value === "") return "—";
  if (ans.input_type === "bool") return ans.value ? "Yes" : "No";
  if (ans.input_type === "number") {
    const n = Number(ans.value);
    return Number.isFinite(n) ? String(n) : String(ans.value);
  }
  return String(ans.value);
}

export default function Forms() {
  const { user } = useAuth();
  const { confirm } = useDialog();
  const nav = useNavigate();
  const location = useLocation();
  const canManage = ["owner", "manager"].includes(user?.role);
  const canVerify = ["owner", "manager", "supervisor"].includes(user?.role);

  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeKey, setActiveKey] = useState(null); // template.id or "__manage"
  const [templateModal, setTemplateModal] = useState(null); // { mode, template? }
  const [fillModal, setFillModal] = useState(null); // { template }
  const [qrModal, setQrModal] = useState(null); // { template }
  const [submissions, setSubmissions] = useState([]);
  const [subLoading, setSubLoading] = useState(false);
  const [backlog, setBacklog] = useState("30");
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const activeTemplates = useMemo(() => templates.filter((t) => !t.is_archived), [templates]);
  const activeTemplate = useMemo(
    () => (activeKey && activeKey !== "__manage")
      ? templates.find((t) => t.id === activeKey) || null
      : null,
    [activeKey, templates],
  );

  const loadTemplates = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/forms/templates", { params: { include_archived: canManage } });
      setTemplates(data.templates || []);
      // Pick default tab: first active template, or manage tab if none.
      const active = (data.templates || []).filter((t) => !t.is_archived);
      if (!activeKey || (activeKey !== "__manage" && !active.some((t) => t.id === activeKey))) {
        setActiveKey(active[0]?.id || (canManage ? "__manage" : null));
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load forms");
    } finally {
      setLoading(false);
    }
  };

  const loadSubmissions = async (templateId) => {
    if (!templateId) { setSubmissions([]); return; }
    setSubLoading(true);
    try {
      const params = { template_id: templateId };
      if (backlog !== "all") params.days = Number(backlog);
      const { data } = await api.get("/forms/submissions", { params });
      setSubmissions(data.submissions || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load submissions");
    } finally {
      setSubLoading(false);
    }
  };

  useEffect(() => { loadTemplates(); /* eslint-disable-next-line */ }, []);
  useEffect(() => {
    if (activeKey && activeKey !== "__manage") loadSubmissions(activeKey);
    else setSubmissions([]);
    // eslint-disable-next-line
  }, [activeKey, backlog]);

  // Deep-link support — a scanned form QR (?open=<template_id>) or a
  // sidebar link should jump straight into the Fill modal for the
  // requested template. Fired once templates are loaded so we can
  // validate the id + hydrate the modal with the freshest snapshot.
  //
  // NOTE: `location.search` is in the dependency array so a rescan of
  // the SAME form (which navigates back to /app/forms?open=<id> while
  // the Forms page is already mounted) re-triggers the effect. Without
  // it, the second scan changed the URL but `templates`/`loading` never
  // did, so the modal stayed closed until the user switched tabs and
  // came back. Other QR types didn't hit this because they land on a
  // page (Daily Tracking, Food Prep) that reads search params on every
  // render.
  useEffect(() => {
    if (loading || templates.length === 0) return;
    const params = new URLSearchParams(location.search);
    const openId = params.get("open");
    if (!openId) return;
    const tpl = templates.find((t) => t.id === openId && !t.is_archived);
    if (tpl) {
      setActiveKey(tpl.id);
      setFillModal({ template: tpl });
    } else {
      toast.error("That form is no longer available.");
    }
    // Strip the query param so back-navigation doesn't re-open.
    nav(location.pathname, { replace: true });
    // eslint-disable-next-line
  }, [loading, templates, location.search]);

  const onCreateTemplate = () => setTemplateModal({ mode: "create" });
  const onEditTemplate = (t) => setTemplateModal({ mode: "edit", template: t });
  const onSavedTemplate = async () => { setTemplateModal(null); await loadTemplates(); };

  const onDeleteTemplate = async (t) => {
    const ok = await confirm({
      title: `Delete "${t.name}"?`,
      message: "The form will be archived. Existing submissions stay in the Audit Vault. You can restore it later if needed.",
      danger: true, confirmLabel: "Delete form",
    });
    if (!ok) return;
    try {
      await api.delete(`/forms/templates/${t.id}`);
      toast.success("Form archived");
      // If the deleted tab was active, jump elsewhere.
      if (activeKey === t.id) setActiveKey(null);
      await loadTemplates();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  const onRestoreTemplate = async (t) => {
    try {
      await api.post(`/forms/templates/${t.id}/restore`);
      toast.success("Form restored");
      await loadTemplates();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Restore failed");
    }
  };

  const onPurgeTemplate = async (t) => {
    // Only offered by ManageTab when submission_count === 0. We still
    // ask for confirmation because it's an irreversible operation, and
    // the backend also double-checks the zero-submission invariant so
    // we can't accidentally destroy audit-vault history.
    const ok = await confirm({
      title: `Permanently delete "${t.name}"?`,
      message: "This form was never used, so there are no submissions to lose. It will be removed completely — this cannot be undone.",
      danger: true, confirmLabel: "Delete permanently",
    });
    if (!ok) return;
    try {
      await api.delete(`/forms/templates/${t.id}/purge`);
      toast.success("Form deleted permanently");
      await loadTemplates();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Permanent delete failed");
    }
  };

  const onVerify = async (s) => {
    try {
      await api.put(`/forms/submissions/${s.id}/verify`);
      toast.success("Verified");
      await loadSubmissions(activeKey);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Verify failed");
    }
  };

  const onDeleteSubmission = async (s) => {
    const ok = await confirm({
      title: "Delete this submission?",
      message: "This cannot be undone.",
      danger: true, confirmLabel: "Delete",
    });
    if (!ok) return;
    try {
      await api.delete(`/forms/submissions/${s.id}`);
      toast.success("Submission deleted");
      await loadSubmissions(activeKey);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  const filteredSubs = useMemo(() => {
    let out = submissions;
    if (statusFilter !== "all") out = out.filter((s) => s.status === statusFilter);
    const q = query.trim().toLowerCase();
    if (q) {
      out = out.filter((s) => {
        if ((s.submitted_by_name || "").toLowerCase().includes(q)) return true;
        if ((s.notes || "").toLowerCase().includes(q)) return true;
        return (s.answers || []).some((a) =>
          String(a.value ?? "").toLowerCase().includes(q) ||
          String(a.label ?? "").toLowerCase().includes(q),
        );
      });
    }
    return out;
  }, [submissions, statusFilter, query]);

  const pendingCount = useMemo(
    () => submissions.filter((s) => s.status === "submitted").length,
    [submissions],
  );

  return (
    <div className="space-y-6" data-testid="forms-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <ClipboardList size={22} /> Forms
          </h1>
          <p className="text-sm opacity-70 mt-1">
            Build custom checklists your team uses every day. Each submission is captured, timestamped, and available for audit.
          </p>
        </div>
        {canManage && (
          <button
            className="vb-btn primary"
            onClick={onCreateTemplate}
            data-testid="forms-create-btn"
          >
            <Plus size={16} /> Create new form
          </button>
        )}
      </div>

      {loading ? (
        <div className="vb-card p-6 opacity-70">Loading…</div>
      ) : (
        <>
          {/* Tab bar — matches the app-wide vb-tabs pill container (Equipment, etc.) */}
          <div className="flex justify-between items-center flex-wrap gap-3" data-testid="forms-tabbar">
            <div className="vb-tabs" role="tablist">
              {activeTemplates.map((t) => (
                <button
                  key={t.id}
                  role="tab"
                  aria-selected={activeKey === t.id}
                  onClick={() => setActiveKey(t.id)}
                  className={`vb-tab ${activeKey === t.id ? "active" : ""}`}
                  data-testid={`forms-tab-${t.id}`}
                  title={t.category || ""}
                >
                  {t.name}
                </button>
              ))}
              {canManage && (
                <button
                  role="tab"
                  aria-selected={activeKey === "__manage"}
                  onClick={() => setActiveKey("__manage")}
                  className={`vb-tab ${activeKey === "__manage" ? "active" : ""}`}
                  data-testid="forms-tab-manage"
                >
                  Manage forms
                </button>
              )}
              {activeTemplates.length === 0 && !canManage && (
                <span className="text-sm opacity-70 px-3 py-2">
                  No forms yet — ask your owner or manager to create one.
                </span>
              )}
            </div>
          </div>

          {/* Panel content */}
          {activeKey === "__manage" ? (
            <ManageTab
              templates={templates}
              onEdit={onEditTemplate}
              onDelete={onDeleteTemplate}
              onRestore={onRestoreTemplate}
              onPurge={onPurgeTemplate}
              onCreate={onCreateTemplate}
              onQR={(t) => setQrModal({ template: t })}
            />
          ) : activeTemplate ? (
            <SubmissionPanel
              template={activeTemplate}
              submissions={filteredSubs}
              rawCount={submissions.length}
              pendingCount={pendingCount}
              loading={subLoading}
              backlog={backlog}
              setBacklog={setBacklog}
              query={query}
              setQuery={setQuery}
              statusFilter={statusFilter}
              setStatusFilter={setStatusFilter}
              onFill={() => setFillModal({ template: activeTemplate })}
              onVerify={onVerify}
              onDeleteSubmission={onDeleteSubmission}
              canVerify={canVerify}
              canManage={canManage}
              me={user}
            />
          ) : (
            <div className="vb-card p-6 opacity-70 text-sm">
              {canManage ? "Create your first form to start collecting daily records." : "No form selected."}
            </div>
          )}
        </>
      )}

      {templateModal && (
        <FormTemplateModal
          mode={templateModal.mode}
          template={templateModal.template}
          onClose={() => setTemplateModal(null)}
          onSaved={onSavedTemplate}
        />
      )}
      {fillModal && (
        <FormFillModal
          template={fillModal.template}
          onClose={() => setFillModal(null)}
          onSubmitted={async () => { setFillModal(null); await loadSubmissions(activeKey); }}
        />
      )}
      {qrModal && (
        <FormQRModal
          template={qrModal.template}
          onClose={() => setQrModal(null)}
        />
      )}
    </div>
  );
}

function SubmissionPanel({
  template, submissions, rawCount, pendingCount, loading,
  backlog, setBacklog, query, setQuery, statusFilter, setStatusFilter,
  onFill, onVerify, onDeleteSubmission, canVerify, canManage, me,
}) {
  return (
    <div className="vb-card p-4 space-y-4" data-testid="forms-submission-panel">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-lg font-semibold">{template.name}</h2>
          {template.category && (
            <div className="text-xs opacity-70 mt-0.5">{template.category}</div>
          )}
          <div className="text-xs opacity-70 mt-2 inline-flex items-center gap-2 flex-wrap">
            <span>{template.steps?.length || 0} step(s)</span>
            {template.requires_supervisor_verification && (
              <span className="inline-flex items-center gap-1">
                <ShieldCheck size={12} /> Supervisor verification required
              </span>
            )}
            {pendingCount > 0 && (
              <span className="vb-pill warn text-[10px]" data-testid="forms-pending-pill">
                {pendingCount} awaiting verification
              </span>
            )}
          </div>
        </div>
        <button className="vb-btn primary" onClick={onFill} data-testid="forms-fill-btn">
          <Plus size={16} /> Fill form
        </button>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative">
          <Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 opacity-50" />
          <input
            placeholder="Search submissions…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="vb-input pl-7"
            style={{ minWidth: 240 }}
            data-testid="forms-search"
          />
        </div>
        <div className="flex items-center gap-1" data-testid="forms-status-filter">
          {[
            { key: "all", label: "All" },
            { key: "submitted", label: "Awaiting" },
            { key: "verified", label: "Verified" },
          ].map((f) => (
            <button
              key={f.key}
              onClick={() => setStatusFilter(f.key)}
              className={`vb-chip ${statusFilter === f.key ? "active" : ""}`}
              data-testid={`forms-status-${f.key}`}
            >
              {f.label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-1 ml-auto" data-testid="forms-backlog-filter">
          <ArrowUpDown size={12} className="opacity-50" />
          {BACKLOG_OPTIONS.map((b) => (
            <button
              key={b.key}
              onClick={() => setBacklog(b.key)}
              className={`vb-chip ${backlog === b.key ? "active" : ""}`}
              data-testid={`forms-backlog-${b.key}`}
            >
              {b.label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="text-sm opacity-70">Loading…</div>
      ) : submissions.length === 0 ? (
        <div className="text-sm opacity-70 py-8 text-center border border-dashed rounded-md">
          {rawCount === 0
            ? "No submissions yet. Tap “Fill form” to capture the first entry."
            : "No submissions match this filter."}
        </div>
      ) : (
        <div className="space-y-2" data-testid="forms-submission-list">
          {submissions.map((s) => (
            <SubmissionRow
              key={s.id}
              submission={s}
              onVerify={onVerify}
              onDelete={onDeleteSubmission}
              canVerify={canVerify}
              canManage={canManage}
              me={me}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function SubmissionRow({ submission, onVerify, onDelete, canVerify, canManage, me }) {
  const [open, setOpen] = useState(false);
  const meta = STATUS_META[submission.status] || STATUS_META.submitted;
  const Icon = meta.icon;
  // Any supervisor+ in this tenant may verify — the audit log records
  // who submitted vs who verified so a solo-owner workflow (owner does
  // both) still leaves a defensible trail.
  const canVerifyThis =
    canVerify &&
    submission.status === "submitted" &&
    submission.requires_supervisor_verification;
  const canDeleteThis = canManage;
  const correctives = (submission.answers || [])
    .filter((a) => a.input_type === "bool" && a.value === false && (a.corrective_action || "").trim());
  const hasCorrectives = correctives.length > 0;

  return (
    <div className="border rounded-md" data-testid={`forms-row-${submission.id}`}>
      <div
        className="p-3 flex items-center gap-3 flex-wrap cursor-pointer hover:bg-black/[0.02]"
        onClick={() => setOpen((v) => !v)}
      >
        <span className={meta.cls}>
          <Icon size={12} className="inline mr-1" />
          {meta.label}
        </span>
        {hasCorrectives && (
          <span className="vb-pill err text-[10px] inline-flex items-center gap-1" title={`${correctives.length} corrective action(s) recorded`}>
            <AlertTriangle size={11} /> {correctives.length} corrective
          </span>
        )}
        <div className="text-sm">
          <div><b>{submission.submitted_by_name || "—"}</b></div>
          <div className="text-xs opacity-70">
            Submitted {fmtDT(submission.submitted_at)}
            {submission.verified_at && (
              <> · Verified {fmtDT(submission.verified_at)} by {submission.verified_by_name || "—"}</>
            )}
          </div>
        </div>
        <div className="ml-auto flex items-center gap-2">
          {canVerifyThis && (
            <button
              className="vb-btn"
              onClick={(e) => { e.stopPropagation(); onVerify(submission); }}
              title="Verify submission"
              data-testid={`forms-verify-${submission.id}`}
            >
              Verify
            </button>
          )}
          {canDeleteThis && (
            <button
              className="vb-btn ghost"
              onClick={(e) => { e.stopPropagation(); onDelete(submission); }}
              title="Delete submission"
              data-testid={`forms-delete-${submission.id}`}
            >
              <Trash2 size={14} />
            </button>
          )}
        </div>
      </div>
      {open && (
        <div className="border-t p-3 space-y-2 text-sm bg-black/[0.02]" data-testid={`forms-detail-${submission.id}`}>
          {(submission.answers || []).map((a) => (
            <div key={a.step_id} className="space-y-1">
              <div className="flex items-start gap-3">
                <div className="w-1/2 opacity-70">
                  {a.label}
                  {a.required && <span className="text-red-500 ml-0.5">*</span>}
                </div>
                <div className="w-1/2"><b>{fmtAnswer(a)}</b></div>
              </div>
              {a.input_type === "bool" && a.value === false && (a.corrective_action || "").trim() && (
                <div className="ml-0 md:ml-4 p-2 rounded border-l-4 bg-red-50" style={{ borderColor: "#b91c1c" }}>
                  <div className="text-xs font-semibold text-red-800 inline-flex items-center gap-1">
                    <AlertTriangle size={11} /> Corrective action
                  </div>
                  <div className="text-xs text-red-900">{a.corrective_action}</div>
                </div>
              )}
            </div>
          ))}
          {submission.notes && (
            <div className="pt-2 mt-2 border-t border-black/10 text-xs opacity-80">
              <b>Notes:</b> {submission.notes}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ManageTab({ templates, onEdit, onDelete, onRestore, onPurge, onCreate, onQR }) {
  const active = templates.filter((t) => !t.is_archived);
  const archived = templates.filter((t) => t.is_archived);
  return (
    <div className="vb-card p-4 space-y-6" data-testid="forms-manage-panel">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">Manage forms</h2>
        <button className="vb-btn primary" onClick={onCreate} data-testid="forms-manage-create">
          <Plus size={16} /> Create new form
        </button>
      </div>

      <div>
        <h3 className="text-sm font-semibold opacity-70 mb-2">Active</h3>
        {active.length === 0 ? (
          <div className="text-sm opacity-70 border border-dashed rounded-md p-6 text-center">
            No forms yet. Create one to start collecting.
          </div>
        ) : (
          <div className="space-y-2">
            {active.map((t) => (
              <div key={t.id} className="border rounded-md p-3 flex items-center gap-3 flex-wrap" data-testid={`forms-manage-row-${t.id}`}>
                <div className="min-w-0">
                  <div className="font-medium truncate">{t.name}</div>
                  <div className="text-xs opacity-70">
                    {t.category ? <>{t.category} · </> : null}
                    {t.steps?.length || 0} step(s)
                    {t.requires_supervisor_verification && <> · Requires verify</>}
                  </div>
                </div>
                <div className="ml-auto flex items-center gap-2">
                  <button
                    className="vb-btn ghost"
                    onClick={() => onQR(t)}
                    title="Show printable QR sticker for this form"
                    data-testid={`forms-manage-qr-${t.id}`}
                  >
                    <QrCode size={14} /> QR
                  </button>
                  <button className="vb-btn ghost" onClick={() => onEdit(t)} data-testid={`forms-manage-edit-${t.id}`}>
                    <Pencil size={14} /> Edit
                  </button>
                  <button className="vb-btn ghost" onClick={() => onDelete(t)} data-testid={`forms-manage-delete-${t.id}`}>
                    <Trash2 size={14} /> Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {archived.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold opacity-70 mb-2">Archived</h3>
          <div className="space-y-2">
            {archived.map((t) => {
              // A form that was never filled can be permanently deleted
              // without touching any audit-vault history. Anything with
              // at least one submission has to stay archived so the
              // vault trail remains intact — we hide the Delete
              // permanently button in that case and the backend
              // refuses too (409) as a safety net.
              const unused = (t.submission_count ?? 0) === 0;
              return (
                <div key={t.id} className="border rounded-md p-3 flex items-center gap-3 flex-wrap opacity-80" data-testid={`forms-archived-row-${t.id}`}>
                  <div className="min-w-0">
                    <div className="font-medium">{t.name}</div>
                    <div className="text-xs opacity-80">
                      {t.steps?.length || 0} step(s) ·{" "}
                      {unused
                        ? <span data-testid={`forms-archived-count-${t.id}`}>Never used</span>
                        : <span data-testid={`forms-archived-count-${t.id}`}>{t.submission_count} submission{t.submission_count === 1 ? "" : "s"}</span>}
                    </div>
                  </div>
                  <div className="ml-auto flex items-center gap-2">
                    <button className="vb-btn ghost" onClick={() => onRestore(t)} data-testid={`forms-restore-${t.id}`}>
                      Restore
                    </button>
                    {unused && (
                      <button
                        className="vb-btn ghost"
                        onClick={() => onPurge(t)}
                        title="Delete this unused form completely"
                        data-testid={`forms-purge-${t.id}`}
                        style={{ color: "var(--vb-error, #b91c1c)" }}
                      >
                        <Trash2 size={14} /> Delete permanently
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
